import styled, { ColorsTypes, useTheme } from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, ButtonIcon, Flex, Hr, Icons, Label, Page, Radio, RichContent, Section2, Typography } from 'components';
import { createMarkup, createUrl, request } from 'app/utils';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useSchool } from 'features/school';
import { StudentPracticeExamAssessmentStatusEnum, ExamsPagination, ExamsQuestionItemState, useExams } from 'features/exams';
import Player from 'features/player';
import { IconsTypes } from 'components/icons';
import QuestionSkeleton from 'features/exams/components/questionSkeleton';

const Icon = styled(Icons)`
  width: 24px;
  color: ${(props: any) => props.theme.colors[props['data-color']]};
  flex-shrink: 0;
`;

const QuestioNumber = styled(Typography)`
  position: relative;

  &::before {
    content: '';
    display: block;
    background: ${({ theme }) => theme.colors.primary_400};
    position: absolute;
    left: calc(100% + 5px);

    ${({ theme: { mediaQueries } }) => {
      return `
        width: 5px;
        height: 5px;
        top: 11px;

        ${mediaQueries.desktop} {
          width: 8px;
          height: 8px;
          top: 20px;
        }
      `;
    }}
  }
`;

const Question = () => {
  const { currentTheme } = useTheme();
  const [loading, setLoading] = useState(true);
  // const [loadingEdit, setLoadingEdit] = useState(false);
  const [answerChoiceUuid, setAnswerChoiceUuid] = useState('');

  const { examUuid, assessmentUuid, questionUuid } = useParams();
  const navigate = useNavigate();

  const { apiGatewayUrl } = useSchool();
  const {
    examsDetails,
    question,
    questionCache,
    questions,
    setExamsQuestion,
    clearExamsQuestion,
    setExamsQuestionCache,
    getQuestionNavigation,
    sendQuestionAnswer,
    setExamsAction,
  } = useExams();

  const letter = ['a', 'b', 'c', 'd'];

  const hasResult = typeof question?.rightAnswer === 'boolean';
  const isPlaying = examsDetails?.assessmentStatus === StudentPracticeExamAssessmentStatusEnum.StartedNotFinished;

  const fetchQuestion = useCallback(
    async (questionUuid: string, cb: (question: ExamsQuestionItemState) => void) => {
      await request<ExamsQuestionItemState>({
        url: `/api/v1/School/PracticeExam/Question/${questionUuid}`,
        method: 'get',
        baseURL: apiGatewayUrl,
      }).then(({ data }) => {
        if (cb) cb(data);
      });
    },
    [questionCache]
  );

  const getNextQuestion = useCallback(
    async (questionUuid: string) => {
      const navigation = getQuestionNavigation(questionUuid);

      if (navigation.next) {
        if (!questionCache[navigation.next.studentPracticeExamAssessmentQuestionUuid]) {
          await fetchQuestion(navigation.next.studentPracticeExamAssessmentQuestionUuid, (question) => {
            setExamsQuestionCache(question);
          });
        } else {
          getNextQuestion(navigation.next.studentPracticeExamAssessmentQuestionUuid);
        }
      }
    },
    [questionUuid, questionCache, questions]
  );

  const getQuestion = useCallback(async () => {
    setLoading(true);

    if (questionCache[questionUuid]) {
      setExamsQuestion(questionCache[questionUuid]);
      setLoading(false);

      setTimeout(async () => {
        getNextQuestion(questionUuid);
      }, 1000);
    } else {
      await fetchQuestion(questionUuid, (question) => {
        setExamsQuestion(question);
        setExamsQuestionCache(question);
        setLoading(false);

        setTimeout(() => getNextQuestion(questionUuid), 1500);
      });
    }
  }, [questionUuid, questionCache, examsDetails]);

  const questionNavigation = getQuestionNavigation(questionUuid);

  // const handleSendQuestionAnswer2 = useCallback(() => {
  //   setLoadingEdit(true);

  //   if (answerChoiceUuid) {
  //     sendQuestionAnswer(assessmentUuid, questionUuid, answerChoiceUuid, () => {
  //       if (questionNavigation.next) {
  //         navigate(createUrl('exams/question', [examUuid, assessmentUuid, questionNavigation.next.studentPracticeExamAssessmentQuestionUuid]));
  //       } else {
  //         if (!questionNavigation.next) {
  //           setExamsAction('finish');
  //         }
  //       }

  //       setLoadingEdit(false);
  //     });
  //   } else {
  //     setExamsAction('finish');

  //     setLoadingEdit(false);
  //   }
  // }, [assessmentUuid, questionUuid, answerChoiceUuid, questionNavigation]);

  const handleSendQuestionAnswer = useCallback(
    (answerChoiceUuid: string) => {
      setAnswerChoiceUuid(answerChoiceUuid);

      sendQuestionAnswer(assessmentUuid, questionUuid, answerChoiceUuid, async () => {
        await fetchQuestion(questionUuid, (question) => {
          setExamsQuestionCache(question);
        });
      });
    },
    [assessmentUuid, questionUuid]
  );

  useEffect(() => {
    setAnswerChoiceUuid(question?.questionAnswerChoiceUuid);
  }, [question]);

  useEffect(() => {
    if (questionUuid) getQuestion();

    return () => {
      clearExamsQuestion();
    };
  }, [questionUuid]);

  return (
    <Page title="Questão">
      <ExamsPagination />
      <Flex flexGrow={0} my={{ m: 40, d: 60 }} flexDirection="column">
        {loading ? (
          <Section2>
            <QuestionSkeleton />
          </Section2>
        ) : (
          <>
            <Section2 flexWrap="nowrap">
              <Flex flexGrow={0} px={{ m: 0, d: 30 }} flexWrap="nowrap" flexDirection={{ m: 'column', d: 'row' }}>
                <Flex flexGrow={0} flexShrink={0} position="relative">
                  <QuestioNumber type="type6" fontWeight="bold" color="color100">
                    {String(question?.order).padStart(2, '0')}
                  </QuestioNumber>
                </Flex>

                <Flex flexDirection="column" pt={{ d: '10px' }} ml={{ m: 0, d: 3 }} mt={{ m: 2, d: 0 }}>
                  <Typography type="type3" color="color100" lineHeight="1.5" mx={{ d: 3 }}>
                    <div dangerouslySetInnerHTML={createMarkup(question?.questionText)} />
                  </Typography>
                  {question?.isQuestionInvalidated && (
                    <Typography type="type1" fontWeight="bold" color="warning_400" lineHeight="1.5" mx={{ d: 3 }} mt={2}>
                      Esta questão foi invalidada, e as respostas foram ajustadas para refletir uma avaliação justa para todos os alunos.
                    </Typography>
                  )}

                  <Flex flexDirection="column" mt={5}>
                    {question?.answers.map((answer, index) => {
                      let result: [IconsTypes, ColorsTypes] = undefined!;

                      // Simulado finalizado
                      if (!isPlaying) {
                        // Se deve mostrar resultado
                        if (question?.rightQuestionAnswerChoiceUuid) {
                          // Se resposta certa
                          if (answer?.uuid === question?.rightQuestionAnswerChoiceUuid) {
                            result = ['checkedOutline', 'primary_400'];
                          }

                          // Se minha resposta estiver errada
                          else if (answer?.uuid === question?.questionAnswerChoiceUuid) {
                            result = ['closeCircle', 'alert_400'];
                          }

                          // Outras alternativas
                          else {
                            result = ['radioboxBlank', 'color30'];
                          }
                        } else {
                          // Se foi a minha resposta
                          if (answer?.uuid === question?.questionAnswerChoiceUuid) {
                            result = ['radioboxMarked', 'color100'];
                          } else {
                            result = ['radioboxBlank', 'color100'];
                          }
                        }
                      }

                      return (
                        <Flex
                          key={`question-${question?.uuid}-answer-${answer?.uuid}`}
                          bg={answer?.uuid === answerChoiceUuid ? 'quizActiveQuestionBg' : ''}
                          px={{ m: 2, d: 3 }}
                          py={{ m: 2, d: 3 }}
                          flexWrap="nowrap"
                          borderRadius={4}
                          alignItems="flex-start"
                        >
                          {isPlaying ? (
                            <Radio
                              id={`answer-${answer?.uuid}`}
                              name="answer"
                              value={answer?.uuid}
                              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                handleSendQuestionAnswer(answer?.uuid);
                              }}
                              checked={answer?.uuid === answerChoiceUuid}
                              color="color100"
                            />
                          ) : (
                            !!result && <Icon icon={result[0]} data-color={result[1]} />
                          )}

                          <Flex
                            flexWrap="nowrap"
                            ml={2}
                            pt="2px"
                            // onClick={() => {
                            //   if (isPlaying) {
                            //     setAnswerChoiceUuid(answer.uuid);
                            //   }
                            // }}
                            style={{ cursor: hasResult ? 'inherit' : 'pointer' }}
                          >
                            <Flex flexGrow={0}>
                              <Typography color={result ? result[1] : 'color100'}>{letter[index]})</Typography>
                            </Flex>

                            <Flex flexGrow={1} ml={1}>
                              <Typography color={result ? result[1] : 'color100'}>
                                <Label htmlFor={`answer-${answer?.uuid}`}>
                                  <div dangerouslySetInnerHTML={createMarkup(answer?.questionAnswerText)} />
                                </Label>
                              </Typography>
                            </Flex>
                          </Flex>
                        </Flex>
                      );
                    })}
                  </Flex>
                </Flex>
              </Flex>
            </Section2>

            {hasResult ? (
              <Flex bg={currentTheme === 'dark' ? 'gray_600' : 'gray_200'} my={5} py={6}>
                <Section2 flexDirection="column" pl={{ m: 0, d: 100 }} pr={{ m: 0, d: 30 }}>
                  <Flex flexGrow={0} alignItems="center" mb={3}>
                    <Icon
                      data-color={question?.rightAnswer ? 'primary_400' : 'alert_400'}
                      icon={!question?.questionAnswerChoiceUuid ? 'radioboxBlank' : question?.rightAnswer ? 'checkedOutline' : 'closeCircle'}
                    />
                    <Typography type="type3" upper fontWeight="bold" color={question?.rightAnswer ? 'primary_400' : 'alert_400'} ml={1}>
                      {question?.rightAnswer ? 'Resposta certa' : !question?.questionAnswerChoiceUuid ? 'Não respondida' : 'Resposta errada'}
                    </Typography>
                  </Flex>

                  {!!question?.commentedAnswer && (
                    <RichContent defaultType="type3" currentTheme={currentTheme}>
                      {question?.commentedAnswer}
                    </RichContent>
                  )}

                  {Boolean(question?.externalVideoProviderIdOrUrl) && (
                    <Flex mt={{ m: 30 }} flexGrow={0} justifyContent="center">
                      <Player
                        videoProvider={question?.videoProvider ? question?.videoProvider : 1}
                        externalVideoProviderIdOrUrl={question?.externalVideoProviderIdOrUrl ? question?.externalVideoProviderIdOrUrl : ''}
                        protectedVideoOtp={question?.protectedVideoOtp ? question?.protectedVideoOtp : ''}
                        protectedVideoPlaybackInfo={question?.protectedVideoPlaybackInfo ? question?.protectedVideoPlaybackInfo : ''}
                        protectedVideoPlayerTheme={question?.protectedVideoPlayerTheme ? question?.protectedVideoPlayerTheme : ''}
                        width={{ m: 320, t: 640, d: '100%' }}
                        height={{ m: 180, t: 360, d: 576 }}
                        maxWidth={{ d: 1024 }}
                        maxHeight={{ d: 576 }}
                      />
                    </Flex>
                  )}
                </Section2>
              </Flex>
            ) : (
              <>
                <Hr color="quizActiveQuestionBg" my={5} />
              </>
            )}
          </>
        )}

        <Flex flexGrow={1} justifyContent="center" pb={{ d: 60 }}>
          {!!questionNavigation.prev && (
            <ButtonIcon
              disabled={!questionNavigation.prev}
              icon="arrowLeft"
              onClick={() =>
                navigate(createUrl('exams/question', [examUuid, assessmentUuid, questionNavigation.prev.studentPracticeExamAssessmentQuestionUuid]))
              }
              bg={questionNavigation.prev ? 'primary_400' : 'gray_500'}
              color="dark_350"
              width={40}
              height={40}
              borderRadius={4}
              title="Pergunta anterior"
            />
          )}

          {isPlaying ? (
            <>
              {questionNavigation.next ? (
                <Button
                  onClick={() => {
                    navigate(
                      createUrl('exams/question', [examUuid, assessmentUuid, questionNavigation.next.studentPracticeExamAssessmentQuestionUuid])
                    );
                  }}
                  bg="primary_400"
                  title="Próxima pergunta"
                  square
                  color="dark_350"
                  minWidth={230}
                  ml={2}
                >
                  Próxima pergunta
                </Button>
              ) : (
                <Button
                  bg="primary_400"
                  onClick={() => {
                    setExamsAction('finish');
                  }}
                  title="Finalizar avaliação"
                  square
                  color="dark_350"
                  minWidth={230}
                  ml={2}
                >
                  Finalizar avaliação
                </Button>
              )}
            </>
          ) : (
            <>
              {!!questionNavigation.next && (
                <Button
                  onClick={() => {
                    if (questionNavigation.next) {
                      navigate(
                        createUrl('exams/question', [examUuid, assessmentUuid, questionNavigation.next.studentPracticeExamAssessmentQuestionUuid])
                      );
                    } else {
                      navigate(createUrl('exams/result', [examUuid, assessmentUuid]));
                    }
                  }}
                  bg={'primary_400'}
                  color="dark_350"
                  title={questionNavigation.next ? 'Próxima pergunta' : 'Resultado'}
                  square
                  ml={2}
                >
                  {questionNavigation.next ? 'Próxima pergunta' : 'Resultado'}
                </Button>
              )}

              {hasResult && (
                <Button
                  onClick={() => {
                    navigate(createUrl('exams/result', [examUuid, assessmentUuid]));
                  }}
                  bg={'primary_400'}
                  color="dark_350"
                  title="Resultado"
                  square
                  ml={2}
                  mt={{ m: questionNavigation.next ? 2 : 0, t: 0 }}
                >
                  Ver Resultado
                </Button>
              )}
            </>
          )}
        </Flex>
      </Flex>
    </Page>
  );
};

export default Question;
